import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Modal, ModalBody, ModalFooter,
  Spinner, Row, Col, FormGroup, Label, Input,
  Carousel, CarouselItem, CarouselControl, CarouselIndicators, CarouselCaption,
} from 'reactstrap';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import { Tooltip } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
  requestInspectionUploadUrl,
  confirmInspectionUpload,
  getInspectionAttachmentSignedUrls,
  updateItemCompletion,
  skipItemCycle,
  removeInspectionAttachment,
  getInspectionHistory,
} from './FormikHelpers/Api';
import { defaultInspectionItem } from './FormikHelpers/InitialValues';
import './inspection-tracker.scss';

const MAX_FILES_PER_ITEM = 5;
const MAX_FILE_SIZE = 120 * 1024 * 1024;
const MAX_ITEMS = 20;
const ALLOWED_FILE_TYPES = 'image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm,video/x-msvideo,video/x-matroska';
// An invoice is a PDF or a photo of the paper copy.
const INVOICE_FILE_TYPES = 'application/pdf,image/jpeg,image/jpg,image/png,image/webp';
const PDF_TYPE = 'application/pdf';

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
};
const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const todayIso = () => new Date().toISOString().split('T')[0];
const escapeHtml = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
const isPdfKey = (k) => /\.pdf$/i.test(k || '');
const isVideoKey = (k) => /\.(mp4|mov|webm|avi|mkv)$/i.test(k || '');

// A cycle closed without the work being done. Legacy rows carry no `outcome`
// and count as completed whenever a completedDate is set.
const isSkipped = (item) => item?.completed?.outcome === 'skipped';
const isCompleted = (item) => !!item?.completed?.completedDate && !isSkipped(item);

// Where an item stands. The server sends `status` on every item; the fallback
// mirrors its rule for records that predate it. The due date is always the
// NEXT due date, so an item is overdue whenever that date has passed.
export const itemStatus = (item) => {
  if (item?.status) return item.status;
  if (!item?.dueDate) return 'none';
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const due = new Date(item.dueDate); due.setHours(0, 0, 0, 0);
  const diff = Math.ceil((due - now) / 86400000);
  if (diff < 0) return 'overdue';
  const band = Math.max(7, Math.min(90, Math.floor((Number(item.frequencyDays) || 365) / 2)));
  if (diff <= band) return 'due_soon';
  if (isSkipped(item)) return 'skipped';
  return isCompleted(item) ? 'done' : 'scheduled';
};

// "by in-house staff" / "by contractor" / "automatically from N suite inspections".
const performedByLabel = (performedBy, source, totalUnits) => {
  if (performedBy === 'system' || source === 'suite_inspections') return `automatically from ${Number(totalUnits) || 0} suite inspections`;
  if (performedBy === 'contractor') return 'by contractor';
  if (performedBy === 'in_house') return 'by in-house staff';
  return '';
};

// Where the due date lands when this cycle is skipped. Same rule as the
// server: add one cycle to the current due date, and keep adding until the
// result is after today (UTC dates).
const rollForwardPreview = (item) => {
  if (!item?.dueDate) return null;
  const next = new Date(item.dueDate);
  if (Number.isNaN(next.getTime())) return null;
  const step = Math.max(1, Math.round(Number(item.frequencyDays) || 365));
  const today = new Date(); today.setUTCHours(0, 0, 0, 0);
  let guard = 0;
  do {
    next.setUTCDate(next.getUTCDate() + step);
    guard += 1;
  } while (next.getTime() <= today.getTime() && guard < 200);
  return next;
};

// One file picked in a dropzone but not uploaded yet.
const toPendingFile = (file) => ({ file, name: file.name, type: file.type, preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null });
const revokePreview = (f) => { if (f?.preview) URL.revokeObjectURL(f.preview); };

const TIER_LABEL = {
  heads_up: 'Heads up', reminder: 'Reminder', action: 'Action needed', urgent: 'Urgent', due_today: 'Due today', overdue: 'Overdue',
};

const buildSelectStyles = () => ({
  menuPortal: (b) => ({ ...b, zIndex: 99999 }),
  container: (b) => ({ ...b, width: '100%' }),
  control: (b, { isFocused }) => ({
    ...b,
    minHeight: 36,
    fontSize: '0.82rem',
    borderRadius: 8,
    borderColor: isFocused ? '#1A6B4A' : '#D4E8DC',
    borderWidth: '1.5px',
    boxShadow: isFocused ? '0 0 0 3px #E8F5EE' : 'none',
    '&:hover': { borderColor: '#1A6B4A' },
    backgroundColor: '#F7FAF8',
  }),
  valueContainer: (b) => ({ ...b, padding: '0 12px' }),
  dropdownIndicator: (b) => ({ ...b, padding: '0 8px', color: '#6B8F7C' }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: (b) => ({ ...b, fontSize: '0.82rem', borderRadius: 8, zIndex: 9999, boxShadow: '0 12px 40px rgba(26,107,74,0.12)', border: '1px solid #D4E8DC' }),
  option: (b, { isSelected, isFocused }) => ({
    ...b,
    backgroundColor: isSelected ? '#1A6B4A' : isFocused ? '#E8F5EE' : 'white',
    color: isSelected ? 'white' : '#1A2D24',
    fontWeight: isSelected ? 600 : 400,
    padding: '8px 12px',
    borderRadius: 5,
  }),
  singleValue: (b) => ({ ...b, color: '#1A2D24', fontWeight: 600 }),
  placeholder: (b) => ({ ...b, color: '#6B8F7C' }),
});

const MediaCarouselModal = ({ isOpen, toggle, items, initialIndex = 0 }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => { if (isOpen) setActiveIndex(initialIndex); }, [isOpen, initialIndex]);

  const next = () => { if (!animating) setActiveIndex((i) => (i === items.length - 1 ? 0 : i + 1)); };
  const prev = () => { if (!animating) setActiveIndex((i) => (i === 0 ? items.length - 1 : i - 1)); };

  if (!isOpen || !items?.length) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" className="it-carousel-modal" style={{ zIndex: 99999 }}>
      <div className="it-carousel-modal__header">
        <div className="it-carousel-modal__header-left">
          <div className="it-carousel-modal__icon"><FeatherIcon icon="film" size={15} color="white" /></div>
          <span className="it-carousel-modal__title">Media Preview</span>
          <span className="it-carousel-modal__count">{items.length} item{items.length !== 1 ? 's' : ''}</span>
        </div>
        <button type="button" className="it-icon-btn" onClick={toggle} aria-label="Close preview"><FeatherIcon icon="x" size={14} color="white" /></button>
      </div>
      <ModalBody className="p-0" style={{ backgroundColor: '#0A1A12' }}>
        <Carousel activeIndex={activeIndex} next={next} previous={prev} pause={false} interval={false}>
          <CarouselIndicators items={items} activeIndex={activeIndex} onClickHandler={(i) => { if (!animating) setActiveIndex(i); }} />
          {items.map((item, idx) => (
            <CarouselItem onExiting={() => setAnimating(true)} onExited={() => setAnimating(false)} key={idx}>
              <div style={{ textAlign: 'center', minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0A1A12' }}>
                {item.type === 'video'
                  ? <video src={item.src} controls autoPlay style={{ maxHeight: '70vh', width: '100%', objectFit: 'contain' }} />
                  : <img src={item.src} alt={item.caption} style={{ maxHeight: '70vh', width: '100%', objectFit: 'contain' }} />}
              </div>
              <CarouselCaption captionText={item.caption} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} />
            </CarouselItem>
          ))}
          <CarouselControl direction="prev" directionText="Prev" onClickHandler={prev} />
          <CarouselControl direction="next" directionText="Next" onClickHandler={next} />
        </Carousel>
      </ModalBody>
      <div className="it-carousel-modal__footer">
        <button type="button" className="it-btn it-btn--ghost-light" onClick={toggle}>Close</button>
      </div>
    </Modal>
  );
};

// Upload each pending file and attach it to the item. `kind` picks the list
// it joins on the server: 'photo' (proof) or 'invoice'.
const uploadAttachmentFiles = async (inspectionId, inspectionIndex, pendingFiles, existingKeys = [], kind = 'photo') => {
  const keys = [];
  for (const { file, name } of pendingFiles) {
    try {
      const { presignedUrl, key } = await requestInspectionUploadUrl(inspectionId, name, file.type, 'General');
      await fetch(presignedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      await confirmInspectionUpload(inspectionId, key, inspectionIndex, kind);
      keys.push(key);
    } catch (err) {
      console.error(`Upload failed for file ${name}:`, err);
    }
  }
  return [...existingKeys, ...keys];
};

// A small tile for a file that is picked but not uploaded yet.
const PendingThumb = ({ file: f, onRemove, disabled }) => (
  <div className="it-attachment__thumb it-attachment__thumb--pending" title={f.name}>
    {f.type === PDF_TYPE
      ? <div className="it-attachment__thumb-pdf"><FeatherIcon icon="file-text" size={14} color="#1A6B4A" /></div>
      : f.type.startsWith('image/')
        ? <img src={f.preview} alt="" className="it-attachment__thumb-img" />
        : <div className="it-attachment__thumb-video"><FeatherIcon icon="video" size={14} color="white" /></div>}
    {!disabled && (
      <button type="button" className="it-attachment__remove-btn" onClick={onRemove} aria-label="Remove pending file">
        <FeatherIcon icon="x" size={8} color="white" />
      </button>
    )}
  </div>
);

// Files for one item. `kind` is 'photo' (proof photos and videos) or
// 'invoice' (a PDF or a photo). In edit mode files go straight up (the server
// records them in the history); for a brand-new record they wait for the
// record to be created. Works before or after the completion is recorded.
const AttachmentUploader = ({ itemIndex, existingAttachments = [], onAttachmentsChange, onPendingChange, inspectionId, isEditMode, readOnly, kind = 'photo', title, helper }) => {
  const isInvoice = kind === 'invoice';
  const [pendingFiles, setPendingFiles] = useState([]);
  const [signedUrls, setSignedUrls] = useState({});
  const [uploading, setUploading] = useState(false);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselStart, setCarouselStart] = useState(0);
  const fetchedRef = useRef(new Set());

  // For a brand-new record the files wait here until it is created; the modal
  // needs to know about them so it can upload them right after the create.
  useEffect(() => { onPendingChange?.(pendingFiles); }, [pendingFiles, onPendingChange]);

  useEffect(() => {
    const missing = existingAttachments.filter((k) => !signedUrls[k] && !fetchedRef.current.has(k));
    if (!missing.length) return;
    missing.forEach((k) => fetchedRef.current.add(k));
    getInspectionAttachmentSignedUrls(missing)
      .then((urls) => setSignedUrls((prev) => ({ ...prev, ...urls })))
      .catch(() => toast.error('Some previews failed to load'));
  }, [existingAttachments, signedUrls]);

  useEffect(() => () => pendingFiles.forEach((f) => f.preview && URL.revokeObjectURL(f.preview)), [pendingFiles]);

  useEffect(() => {
    if (pendingFiles.length > 0 && inspectionId && !uploading && isEditMode && !readOnly) {
      (async () => {
        setUploading(true);
        const updatedKeys = await uploadAttachmentFiles(inspectionId, itemIndex, pendingFiles, existingAttachments, kind);
        setPendingFiles([]);
        setUploading(false);
        onAttachmentsChange(updatedKeys);
        if (updatedKeys.length > existingAttachments.length) {
          toast.success(`${updatedKeys.length - existingAttachments.length} file(s) uploaded`);
        }
      })();
    }
  }, [pendingFiles.length, inspectionId, uploading, isEditMode, readOnly, itemIndex, existingAttachments, onAttachmentsChange, pendingFiles, kind]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted, rejected) => {
      if (readOnly) return;
      rejected.forEach(({ file, errors }) => {
        errors.forEach((e) => {
          if (e.code === 'file-too-large') toast.error(`${file.name} exceeds 120 MB`);
          if (e.code === 'file-invalid-type') toast.error(`${file.name}: unsupported type`);
        });
      });
      const rem = MAX_FILES_PER_ITEM - (pendingFiles.length + existingAttachments.length);
      const toAdd = accepted.slice(0, rem);
      if (accepted.length > rem) toast.warn(`Max ${MAX_FILES_PER_ITEM} files per item`);
      if (!toAdd.length) return;
      setPendingFiles((prev) => [...prev, ...toAdd.map(toPendingFile)]);
    },
    accept: isInvoice ? INVOICE_FILE_TYPES : ALLOWED_FILE_TYPES,
    multiple: true,
    maxSize: MAX_FILE_SIZE,
    disabled: readOnly,
  });

  const removePending = (i, e) => {
    if (readOnly) return;
    e.stopPropagation();
    setPendingFiles((p) => { revokePreview(p[i]); return p.filter((_, idx) => idx !== i); });
  };

  const removeUploaded = async (i, e) => {
    if (readOnly) return;
    e.stopPropagation();
    const r = await Swal.fire({ title: 'Remove this file?', text: 'It will be taken off the inspection. The history keeps a note that it was removed.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#DC2626', confirmButtonText: 'Remove', width: 380 });
    if (!r.isConfirmed) return;
    const key = existingAttachments[i];
    if (isEditMode && inspectionId) {
      try {
        await removeInspectionAttachment(inspectionId, itemIndex, key);
      } catch {
        return;
      }
    }
    onAttachmentsChange(existingAttachments.filter((_, idx) => idx !== i));
  };

  const total = existingAttachments.length + pendingFiles.length;
  // PDFs open in a new tab; everything else goes through the carousel.
  const carouselItems = useMemo(() => [
    ...existingAttachments.filter((k) => !isPdfKey(k)).map((k, i) => ({ key: k, src: signedUrls[k] || '', caption: `Attachment ${i + 1}`, type: isVideoKey(k) ? 'video' : 'image' })),
    ...pendingFiles.filter((f) => f.type !== PDF_TYPE).map((f, i) => ({ src: f.preview || '', caption: `Pending ${i + 1}`, type: f.type.startsWith('video/') ? 'video' : 'image' })),
  ], [existingAttachments, pendingFiles, signedUrls]);

  const openExisting = (k) => {
    if (isPdfKey(k)) {
      const url = signedUrls[k];
      if (url) window.open(url, '_blank', 'noopener');
      else toast.info('The file link is still loading');
      return;
    }
    setCarouselStart(Math.max(0, carouselItems.findIndex((c) => c.key === k)));
    setCarouselOpen(true);
  };

  const dropLabel = isDragActive
    ? 'Drop files here'
    : isInvoice ? 'Add invoice (PDF or photo)' : `Add photos or video (${total}/${MAX_FILES_PER_ITEM})`;

  return (
    <div className="it-attachment" onClick={(e) => e.stopPropagation()}>
      {title && <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#4A7060', marginBottom: 4 }}>{title}</div>}

      {carouselItems.length > 0 && (
        <button type="button" className="it-attachment__view-btn mb-1" onClick={() => { setCarouselStart(0); setCarouselOpen(true); }}>
          <FeatherIcon icon="image" size={10} /> {total} file{total !== 1 ? 's' : ''}
        </button>
      )}

      {!readOnly && (
        <div {...getRootProps()} className={`it-attachment__dropzone${isDragActive ? ' it-attachment__dropzone--active' : ''}`}>
          <input {...getInputProps()} />
          <div className="it-attachment__dropzone-inner">
            <FeatherIcon icon={isInvoice ? 'file-text' : 'upload-cloud'} size={14} color={isDragActive ? '#1A6B4A' : '#6B8F7C'} />
            <span className="it-attachment__dropzone-label">{dropLabel}</span>
          </div>
        </div>
      )}

      {(existingAttachments.length > 0 || pendingFiles.length > 0) && (
        <div className="it-attachment__thumbs">
          {existingAttachments.map((k, i) => {
            const url = signedUrls[k];
            const isPdf = isPdfKey(k);
            let inner;
            if (!url) inner = <div className="it-attachment__thumb-loading"><Spinner size="sm" /></div>;
            else if (isPdf) inner = <div className="it-attachment__thumb-pdf"><FeatherIcon icon="file-text" size={14} color="#1A6B4A" /></div>;
            else if (isVideoKey(k)) inner = <div className="it-attachment__thumb-video"><FeatherIcon icon="play" size={14} color="white" /></div>;
            else inner = <img src={url} alt="" className="it-attachment__thumb-img" />;
            return (
              <div
                key={i}
                className="it-attachment__thumb"
                role="button"
                tabIndex={0}
                aria-label={isPdf ? 'Open PDF in a new tab' : 'Preview file'}
                onClick={() => openExisting(k)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openExisting(k); } }}
              >
                {inner}
                {!readOnly && (
                  <button type="button" className="it-attachment__remove-btn" onClick={(e) => removeUploaded(i, e)} aria-label="Remove file">
                    <FeatherIcon icon="x" size={8} color="white" />
                  </button>
                )}
              </div>
            );
          })}
          {!readOnly && pendingFiles.map((f, i) => (
            <PendingThumb key={`p${i}`} file={f} onRemove={(e) => removePending(i, e)} />
          ))}
        </div>
      )}

      {uploading && <div className="it-attachment__uploading"><Spinner size="sm" /> Uploading…</div>}
      {!isEditMode && pendingFiles.length > 0 && !uploading && (
        <div style={{ fontSize: '0.72rem', color: '#6B8F7C', marginTop: 4 }}>Uploads when the record is created</div>
      )}
      {helper && <div style={{ fontSize: '0.7rem', color: '#6B8F7C', marginTop: 2 }}>{helper}</div>}

      {readOnly && total === 0 && (
        <span style={{ fontSize: '0.78rem', color: '#9BB5A8', fontStyle: 'italic' }}>{isInvoice ? 'No invoice' : 'No attachments'}</span>
      )}

      <MediaCarouselModal isOpen={carouselOpen} toggle={() => setCarouselOpen(false)} items={carouselItems} initialIndex={carouselStart} />
    </div>
  );
};

// Completion for a brand-new record (nothing saved yet): a simple toggle that
// travels with the create payload.
const CompletedToggle = ({ item, onChange, readOnly }) => {
  const done = isCompleted(item);
  const toggle = () => {
    if (readOnly) return;
    if (done) {
      onChange({ ...item, completed: item.completed?.attachments?.length ? { ...item.completed, completedDate: '' } : null });
    } else {
      onChange({ ...item, completed: { ...(item.completed || {}), completedDate: todayIso(), notes: item.completed?.notes || '', attachments: item.completed?.attachments || [], contractorInvoice: '' } });
    }
  };

  return (
    <div className="it-completed-section">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: done ? 10 : 0 }}>
        <div
          role="switch"
          aria-checked={done}
          tabIndex={readOnly ? -1 : 0}
          onClick={toggle}
          onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(); } }}
          style={{ width: 36, height: 20, borderRadius: 10, background: done ? '#1A6B4A' : '#D4E8DC', position: 'relative', transition: 'background 0.2s', flexShrink: 0, cursor: readOnly ? 'not-allowed' : 'pointer', opacity: readOnly ? 0.6 : 1 }}
        >
          <div style={{ position: 'absolute', top: 3, left: done ? 18 : 3, width: 14, height: 14, borderRadius: '50%', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s' }} />
        </div>
        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: done ? '#1A6B4A' : '#6B8F7C' }}>
          {done ? 'Completed today' : 'Mark completed today'}
        </span>
      </div>
      {done && (
        <FormGroup className="mb-0">
          <Label style={{ fontSize: '0.78rem', color: '#6B8F7C', fontWeight: 600 }}>Completion notes</Label>
          <Input
            type="textarea" bsSize="sm" rows={2}
            value={item.completed?.notes || ''}
            onChange={(e) => !readOnly && onChange({ ...item, completed: { ...(item.completed || {}), notes: e.target.value } })}
            placeholder="What was done, anything to flag…"
            readOnly={readOnly}
            style={{ borderColor: '#D4E8DC', borderRadius: 7, fontSize: '0.82rem', resize: 'vertical' }}
          />
        </FormGroup>
      )}
    </div>
  );
};

// The proof dialog. A completion needs who did the work, at least one photo,
// and the contractor's invoice when a contractor did it. Files upload first,
// then the completion is recorded; the server enforces the same rules.
const CompletionDialog = ({ isOpen, onClose, item, index, inspectionId, onRecordUpdated }) => {
  const [performedBy, setPerformedBy] = useState('in_house');
  const [date, setDate] = useState(todayIso());
  const [notes, setNotes] = useState('');
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [saving, setSaving] = useState(false);
  const wasOpenRef = useRef(false);
  const pendingRef = useRef({ photos: [], invoices: [] });
  pendingRef.current = { photos: pendingPhotos, invoices: pendingInvoices };

  // Start fresh each time the dialog opens.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setPerformedBy(item?.contractor && item?.completed?.performedBy === 'contractor' ? 'contractor' : 'in_house');
      setDate(todayIso());
      setNotes(item?.completed?.notes || '');
      setPendingPhotos((prev) => { prev.forEach(revokePreview); return []; });
      setPendingInvoices((prev) => { prev.forEach(revokePreview); return []; });
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, item]);

  // Release previews when the dialog goes away for good.
  useEffect(() => () => { [...pendingRef.current.photos, ...pendingRef.current.invoices].forEach(revokePreview); }, []);

  const existingPhotos = (item?.completed?.attachments || []).length;
  const existingInvoices = (item?.completed?.invoices || []).length;
  const photoCount = existingPhotos + pendingPhotos.length;
  const invoiceCount = existingInvoices + pendingInvoices.length;

  const missing = [];
  if (!photoCount) missing.push('At least one photo');
  if (performedBy === 'contractor' && !invoiceCount) missing.push("The contractor's invoice (PDF or photo)");
  if (!date || date > todayIso()) missing.push('A completion date that is not in the future');

  const onDropFor = (setter, current) => (accepted, rejected) => {
    rejected.forEach(({ file, errors }) => {
      errors.forEach((e) => {
        if (e.code === 'file-too-large') toast.error(`${file.name} exceeds 120 MB`);
        if (e.code === 'file-invalid-type') toast.error(`${file.name}: unsupported type`);
      });
    });
    const rem = Math.max(0, MAX_FILES_PER_ITEM - current);
    const toAdd = accepted.slice(0, rem);
    if (accepted.length > rem) toast.warn(`Max ${MAX_FILES_PER_ITEM} files per item`);
    if (!toAdd.length) return;
    setter((prev) => [...prev, ...toAdd.map(toPendingFile)]);
  };
  const removeFrom = (setter, i) => (e) => {
    e.stopPropagation();
    setter((prev) => { revokePreview(prev[i]); return prev.filter((_, idx) => idx !== i); });
  };

  const photoDrop = useDropzone({ onDrop: onDropFor(setPendingPhotos, photoCount), accept: ALLOWED_FILE_TYPES, multiple: true, maxSize: MAX_FILE_SIZE, disabled: saving });
  const invoiceDrop = useDropzone({ onDrop: onDropFor(setPendingInvoices, invoiceCount), accept: INVOICE_FILE_TYPES, multiple: true, maxSize: MAX_FILE_SIZE, disabled: saving });

  const save = async () => {
    if (missing.length || saving) return;
    setSaving(true);
    try {
      if (pendingPhotos.length) await uploadAttachmentFiles(inspectionId, index, pendingPhotos, [], 'photo');
      if (pendingInvoices.length) await uploadAttachmentFiles(inspectionId, index, pendingInvoices, [], 'invoice');
      const record = await updateItemCompletion(inspectionId, index, { completedDate: date, performedBy, notes });
      if (record) onRecordUpdated(record);
      toast.success('Completion recorded');
      onClose();
    } catch (err) {
      // The API layer already showed the server message; add its detail when there is one.
      const problems = err?.response?.data?.problems;
      if (Array.isArray(problems) && problems.length) toast.error(problems.join(' '));
    } finally {
      setSaving(false);
    }
  };

  const renderChoice = (value, icon, label, sub) => {
    const active = performedBy === value;
    return (
      <button type="button" className={`it-proof__choice${active ? ' it-proof__choice--active' : ''}`} aria-pressed={active} onClick={() => setPerformedBy(value)} disabled={saving}>
        <FeatherIcon icon={icon} size={20} color={active ? '#1A6B4A' : '#6B8F7C'} />
        <span className="it-proof__choice-title">{label}</span>
        {sub && <span className="it-proof__choice-sub">{sub}</span>}
      </button>
    );
  };

  const renderDropzone = (dz, icon, label) => (
    <div {...dz.getRootProps()} className={`it-attachment__dropzone${dz.isDragActive ? ' it-attachment__dropzone--active' : ''}`} style={{ padding: '12px 10px' }}>
      <input {...dz.getInputProps()} />
      <div className="it-attachment__dropzone-inner">
        <FeatherIcon icon={icon} size={15} color={dz.isDragActive ? '#1A6B4A' : '#6B8F7C'} />
        <span className="it-attachment__dropzone-label">{dz.isDragActive ? 'Drop files here' : label}</span>
      </div>
    </div>
  );

  const dateId = `it-proof-date-${index}`;
  const notesId = `it-proof-notes-${index}`;

  return (
    <Modal isOpen={isOpen} toggle={saving ? undefined : onClose} centered size="lg" className="it-proof" style={{ zIndex: 99999 }} zIndex={99999} backdrop="static">
      <div className="it-header it-proof__header">
        <div className="it-header__left">
          <div className="it-header__icon-wrap" style={{ width: 34, height: 34 }}><FeatherIcon icon="check-square" size={16} color="#A7F3D0" /></div>
          <div className="it-header__meta">
            <div className="it-header__label">Record completion</div>
            <div className="it-header__title">{item?.inspectionTypeName || 'Inspection'}</div>
          </div>
        </div>
        <button type="button" className="it-icon-btn" onClick={onClose} disabled={saving} aria-label="Close"><FeatherIcon icon="x" size={14} color="white" /></button>
      </div>

      <ModalBody style={{ padding: '18px 22px' }}>
        <div className="it-proof__section-title">Who did the work?</div>
        <div className="it-proof__choices">
          {renderChoice('in_house', 'users', 'In-house staff')}
          {renderChoice('contractor', 'briefcase', 'Contractor', item?.contractor)}
        </div>

        <div className="it-proof__section-title" style={{ marginTop: 16 }}>Photos</div>
        {renderDropzone(photoDrop, 'camera', 'Add photos or video')}
        {pendingPhotos.length > 0 && (
          <div className="it-attachment__thumbs">
            {pendingPhotos.map((f, i) => <PendingThumb key={`${f.name}-${i}`} file={f} onRemove={removeFrom(setPendingPhotos, i)} disabled={saving} />)}
          </div>
        )}
        {existingPhotos > 0 && <div className="it-proof__hint">{existingPhotos} already attached</div>}
        <div className="it-proof__hint">At least one photo is required.</div>

        {performedBy === 'contractor' && (
          <>
            <div className="it-proof__section-title" style={{ marginTop: 16 }}>Invoice</div>
            {renderDropzone(invoiceDrop, 'file-text', 'Add invoice (PDF or photo)')}
            {pendingInvoices.length > 0 && (
              <div className="it-attachment__thumbs">
                {pendingInvoices.map((f, i) => <PendingThumb key={`${f.name}-${i}`} file={f} onRemove={removeFrom(setPendingInvoices, i)} disabled={saving} />)}
              </div>
            )}
            {existingInvoices > 0 && <div className="it-proof__hint">{existingInvoices} already attached</div>}
            <div className="it-proof__hint">Required when a contractor did the work.</div>
          </>
        )}

        <Row className="g-3" style={{ marginTop: 8 }}>
          <Col md={5}>
            <FormGroup className="mb-0">
              <Label for={dateId} style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060' }}>Completion date</Label>
              <Input id={dateId} type="date" bsSize="sm" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} disabled={saving} style={{ borderColor: '#D4E8DC', borderRadius: 7, fontSize: '0.82rem' }} />
            </FormGroup>
          </Col>
          <Col md={7}>
            <FormGroup className="mb-0">
              <Label for={notesId} style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060' }}>Notes</Label>
              <Input id={notesId} type="textarea" bsSize="sm" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What was done, anything to flag…" disabled={saving} style={{ borderColor: '#D4E8DC', borderRadius: 7, fontSize: '0.82rem', resize: 'vertical' }} />
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter className="it-proof__footer">
        <ul className="it-proof__checklist" aria-live="polite">
          {missing.length > 0 ? missing.map((m) => (
            <li key={m}><FeatherIcon icon="alert-circle" size={12} color="#DC2626" /> {m}</li>
          )) : (
            <li><FeatherIcon icon="check-circle" size={12} color="#1A6B4A" /> Everything needed is attached.</li>
          )}
        </ul>
        <div className="it-proof__actions">
          <button type="button" className="it-btn it-btn--cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="it-btn it-btn--primary-solid" onClick={save} disabled={missing.length > 0 || saving}>
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={13} />} Save completion
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

// Completion for a saved record: an action, not a form field. Recording it
// (with proof) or skipping it (with a reason) rolls the due date to the next
// cycle and writes the history straight away, so a manager or technician
// never has to press Save afterwards.
const CompletionPanel = ({ item, index, inspectionId, canAct, onRecordUpdated }) => {
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState(item.completed?.notes || '');
  const [dialogOpen, setDialogOpen] = useState(false);
  useEffect(() => { setNotes(item.completed?.notes || ''); }, [item.completed?.notes]);

  const done = isCompleted(item);
  const skipped = isSkipped(item);
  const notesDirty = (item.completed?.notes || '') !== notes;

  const run = async (body, successText) => {
    setBusy(true);
    try {
      const record = await updateItemCompletion(inspectionId, index, body);
      if (record) onRecordUpdated(record);
      if (successText) toast.success(successText);
    } catch {
      // toast already shown by the API layer
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    const nextDue = rollForwardPreview(item);
    const r = await Swal.fire({
      title: 'Skip this cycle?',
      html: `<div style="font-size:0.95rem;line-height:1.6;text-align:left"><strong>${escapeHtml(item.inspectionTypeName || 'This inspection')}</strong> will show as <strong style="color:#A16207">Skipped</strong> instead of overdue.<br/>The next due date moves to <strong>${fmtDate(nextDue)}</strong> and reminders stop until then.<br/><br/>Why was it not done?</div>`,
      input: 'textarea',
      inputPlaceholder: 'e.g. Building closed for renovation; contractor rescheduled to spring',
      inputAttributes: { maxlength: 1000, 'aria-label': 'Reason' },
      inputValidator: (v) => (!v || !v.trim() ? 'A reason is required' : undefined),
      showCancelButton: true,
      confirmButtonColor: '#A16207',
      confirmButtonText: 'Skip this cycle',
      cancelButtonText: 'Back',
      reverseButtons: true,
    });
    if (!r.isConfirmed) return;
    setBusy(true);
    try {
      const record = await skipItemCycle(inspectionId, index, String(r.value || '').trim());
      if (record) onRecordUpdated(record);
      toast.success('Marked as skipped');
    } catch {
      // toast already shown by the API layer
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    const r = await Swal.fire({
      title: skipped ? 'Reopen this cycle?' : 'Reopen this inspection?',
      text: skipped
        ? 'The skip is cleared and the due date goes back to what it was. Files stay attached.'
        : 'The completion date is cleared and the due date goes back to what it was. Files stay attached.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      confirmButtonText: 'Reopen',
      reverseButtons: true,
    });
    if (!r.isConfirmed) return;
    await run({ completedDate: null }, 'Reopened');
  };

  const c = item.completed || {};
  const doneLabel = performedByLabel(c.performedBy, c.autoSource?.kind, c.autoSource?.totalUnits);
  const photoCount = (c.attachments || []).length;
  const invoiceCount = (c.invoices || []).length;
  const proofLine = [photoCount ? `${photoCount} photo${photoCount === 1 ? '' : 's'}` : '', invoiceCount ? 'invoice' : ''].filter(Boolean).join(' · ');

  // Notes plus Save notes / Reopen: the same block under a completion and under a skip.
  const notesAndActions = (
    <>
      <FormGroup className="mb-2">
        <Label style={{ fontSize: '0.78rem', color: '#6B8F7C', fontWeight: 600 }}>Completion notes</Label>
        <Input
          type="textarea" bsSize="sm" rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={canAct ? 'What was done, anything to flag…' : '—'}
          readOnly={!canAct}
          style={{ borderColor: '#D4E8DC', borderRadius: 7, fontSize: '0.82rem', resize: 'vertical', backgroundColor: canAct ? undefined : '#f8f9fa' }}
        />
      </FormGroup>
      {canAct && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="it-btn it-btn--save" style={{ padding: '6px 12px' }} disabled={busy || !notesDirty} onClick={() => run({ notes }, 'Notes saved')}>
            {busy ? <Spinner size="sm" /> : <FeatherIcon icon="save" size={12} />} Save notes
          </button>
          <button type="button" className="it-btn it-btn--cancel" style={{ padding: '6px 12px' }} disabled={busy} onClick={reopen}>
            <FeatherIcon icon="rotate-ccw" size={12} /> Reopen
          </button>
        </div>
      )}
    </>
  );

  return (
    <div className="it-completed-section">
      {done && (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 10px', borderRadius: 7, background: '#F0FDF4', border: '1px solid #A7F3D0', marginBottom: 8 }}>
            <span style={{ display: 'inline-flex', flexShrink: 0, marginTop: 1 }}><FeatherIcon icon="check-circle" size={14} color="#1A6B4A" /></span>
            <div style={{ fontSize: '0.8rem', color: '#1A6B4A', lineHeight: 1.35 }}>
              <div style={{ fontWeight: 700 }}>Completed {fmtDate(c.completedDate)}</div>
              {c.completedByName && <div style={{ fontSize: '0.72rem', color: '#2E8B62' }}>by {c.completedByName}</div>}
              {doneLabel && <div style={{ fontSize: '0.72rem', color: '#2E8B62' }}>Done {doneLabel}</div>}
              {proofLine && <div style={{ fontSize: '0.72rem', color: '#2E8B62' }}>{proofLine}</div>}
            </div>
          </div>
          {notesAndActions}
        </>
      )}

      {skipped && (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 10px', borderRadius: 7, background: '#FEFCE8', border: '1px solid #FDE68A', marginBottom: 8 }}>
            <span style={{ display: 'inline-flex', flexShrink: 0, marginTop: 1 }}><FeatherIcon icon="skip-forward" size={14} color="#A16207" /></span>
            <div style={{ fontSize: '0.8rem', color: '#A16207', lineHeight: 1.35 }}>
              <div style={{ fontWeight: 700 }}>Skipped {fmtDate(c.skippedAt)}</div>
              {c.skippedByName && <div style={{ fontSize: '0.72rem', color: '#B45309' }}>by {c.skippedByName}</div>}
              {c.skipReason && <div style={{ fontStyle: 'italic', marginTop: 3, color: '#78350F', wordBreak: 'break-word' }}>“{c.skipReason}”</div>}
            </div>
          </div>
          {notesAndActions}
        </>
      )}

      {!done && !skipped && (
        <>
          <div style={{ fontSize: '0.78rem', color: '#6B8F7C', marginBottom: 8 }}>
            Not recorded for this cycle.
            {item.lastCompletedDate && <span> Last done {fmtDate(item.lastCompletedDate)}.</span>}
          </div>
          {canAct ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="it-btn it-btn--primary-solid" style={{ padding: '7px 12px' }} disabled={busy} onClick={() => setDialogOpen(true)}>
                <FeatherIcon icon="check" size={13} /> Record completion
              </button>
              <button type="button" className="it-btn it-btn--skip" disabled={busy || !item.dueDate} title={item.dueDate ? undefined : 'Set a due date first'} onClick={skip}>
                {busy ? <Spinner size="sm" /> : <FeatherIcon icon="skip-forward" size={13} />} Skip this cycle
              </button>
            </div>
          ) : (
            <span style={{ fontSize: '0.72rem', color: '#9BB5A8', fontStyle: 'italic' }}>View only</span>
          )}
        </>
      )}

      <CompletionDialog isOpen={dialogOpen} onClose={() => setDialogOpen(false)} item={item} index={index} inspectionId={inspectionId} onRecordUpdated={onRecordUpdated} />
    </div>
  );
};

const ReminderDaysInput = ({ item, onChange, readOnly }) => {
  const days = item.reminderDays;
  const hasDueDate = !!item.dueDate;

  const extraReminderDate = useMemo(() => {
    if (!days || days < 1 || !hasDueDate) return null;
    const base = new Date(item.dueDate);
    base.setUTCDate(base.getUTCDate() - Number(days));
    return base;
  }, [hasDueDate, item.dueDate, days]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <FeatherIcon icon="bell" size={13} color="#D97706" />
        <Label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6B8F7C', margin: 0 }}>Extra reminder</Label>
        <Tooltip arrow title="The team is reminded on the company ladder (60, 45 and 30 days before, then every 5 days until it is done). This adds one more reminder this many days before the due date." placement="top">
          <span style={{ cursor: 'help', color: '#9BB5A8', fontSize: '0.75rem' }} aria-label="About reminders">ⓘ</span>
        </Tooltip>
      </div>

      {readOnly ? (
        <span style={{ fontSize: '0.78rem', color: '#9BB5A8', fontStyle: 'italic' }}>{days ? `${days} days before due` : 'Company ladder only'}</span>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Input
            type="number" bsSize="sm" value={days || ''} min="1" max="365"
            onChange={(e) => { const v = e.target.value; onChange({ ...item, reminderDays: v === '' ? undefined : Math.max(1, Math.min(365, Math.round(Number(v)))) }); }}
            placeholder="Days before due"
            aria-label="Extra reminder days before due"
            style={{ borderColor: '#D4E8DC', borderRadius: 7, fontSize: '0.82rem', width: 140 }}
          />
          {days && (
            <button type="button" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }} onClick={() => onChange({ ...item, reminderDays: undefined })} aria-label="Clear extra reminder">
              <FeatherIcon icon="x-circle" size={15} color="#DC2626" />
            </button>
          )}
        </div>
      )}

      {extraReminderDate && (
        <div style={{ marginTop: 5, padding: '3px 9px', borderRadius: 6, background: '#FFFBEB', border: '1px solid #FDE68A', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <FeatherIcon icon="clock" size={10} color="#D97706" />
          <span style={{ fontSize: '0.72rem', color: '#92400E', fontWeight: 500 }}>Extra reminder on {fmtDate(extraReminderDate)}</span>
        </div>
      )}

      {item.nextReminder?.date && (
        <div style={{ marginTop: 5, fontSize: '0.72rem', color: '#4A7060' }}>
          Next reminder: {fmtDate(item.nextReminder.date)} ({item.nextReminder.daysBeforeDue >= 0 ? `${item.nextReminder.daysBeforeDue} days before` : `${Math.abs(item.nextReminder.daysBeforeDue)} days after`})
        </div>
      )}
      {item.lastReminder?.sentAt && (
        <div style={{ marginTop: 4, padding: '3px 9px', borderRadius: 6, background: '#E8F5EE', border: '1px solid #A7F3D0', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <FeatherIcon icon="send" size={10} color="#1A6B4A" />
          <span style={{ fontSize: '0.72rem', color: '#1A6B4A', fontWeight: 500 }}>Last sent: {TIER_LABEL[item.lastReminder.tier] || item.lastReminder.tier} · {fmtDate(item.lastReminder.sentAt)}</span>
        </div>
      )}
    </div>
  );
};

const ReadOnlyBanner = ({ canRecordCompletion }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, padding: '8px 14px', marginBottom: 16 }}>
    <FeatherIcon icon={canRecordCompletion ? 'check-square' : 'eye'} size={14} color="#C2410C" />
    <span style={{ fontSize: '0.80rem', color: '#C2410C', fontWeight: 600 }}>
      {canRecordCompletion
        ? 'You can record completions, add notes and attach photos. The schedule itself is set by the company.'
        : 'View only.'}
    </span>
  </div>
);

// ── History timeline ──────────────────────────────────────────────────────────
const ACTION_META = {
  created: { icon: 'plus-circle', color: '#1D4ED8', label: 'Added' },
  completed: { icon: 'check-circle', color: '#1A6B4A', label: 'Completed' },
  skipped: { icon: 'skip-forward', color: '#A16207', label: 'Skipped' },
  renewed: { icon: 'copy', color: '#1D4ED8', label: 'Carried over' },
  reopened: { icon: 'rotate-ccw', color: '#B45309', label: 'Reopened' },
  attachments_added: { icon: 'paperclip', color: '#0D9488', label: 'Files added' },
  attachments_removed: { icon: 'trash-2', color: '#DC2626', label: 'Files removed' },
  notes_updated: { icon: 'edit-3', color: '#4338CA', label: 'Notes updated' },
  item_updated: { icon: 'edit-2', color: '#4338CA', label: 'Updated' },
  due_date_changed: { icon: 'calendar', color: '#B45309', label: 'Due date changed' },
  reminder_sent: { icon: 'bell', color: '#D97706', label: 'Reminder sent' },
};
const FIELD_LABEL = { inspectionTypeName: 'Type', frequencyName: 'Frequency', contractor: 'Contractor', notes: 'Notes', reminderDays: 'Extra reminder days' };

const describeEntry = (h) => {
  const n = (h.attachments || []).length;
  const inv = (h.invoices || []).length;
  const fileWord = h.changes?.kind === 'invoice' ? 'invoice' : 'file';
  switch (h.action) {
    case 'completed': {
      const by = performedByLabel(h.changes?.performedBy, h.changes?.source, h.changes?.totalUnits);
      const files = [n ? `${n} file${n === 1 ? '' : 's'}` : '', inv ? `${inv} invoice${inv === 1 ? '' : 's'}` : ''].filter(Boolean).join(', ');
      return `Completed on ${fmtDate(h.completedDate)}${h.completedBy?.displayName ? ` by ${h.completedBy.displayName}` : ''}${by ? ` · ${by}` : ''}${h.nextDueDate ? ` · next due ${fmtDate(h.nextDueDate)}` : ''}${files ? ` · ${files}` : ''}${h.notes ? ` · “${h.notes}”` : ''}`;
    }
    case 'skipped':
      return `Skipped${h.notes ? ` · “${h.notes}”` : ''}${h.nextDueDate ? ` · next due ${fmtDate(h.nextDueDate)}` : ''}`;
    case 'renewed':
      return `Copied from ${h.changes?.fromYear || '—'}${h.nextDueDate ? ` · due ${fmtDate(h.nextDueDate)}` : ''}`;
    case 'reopened':
      return `${h.changes?.was === 'skipped' ? 'Skip' : `Completion of ${fmtDate(h.completedDate)}`} reopened${h.changes?.dueDateRestoredTo ? ` · due date back to ${fmtDate(h.changes.dueDateRestoredTo)}` : ''}`;
    case 'attachments_added': {
      const added = (h.changes?.added || []).length || n;
      return `${added} ${fileWord}${added === 1 ? '' : 's'} attached`;
    }
    case 'attachments_removed': {
      const removed = (h.changes?.removed || []).length || n;
      return `${removed} ${fileWord}${removed === 1 ? '' : 's'} removed`;
    }
    case 'notes_updated':
      return h.changes?.to ? `Notes: “${h.changes.to}”` : 'Notes cleared';
    case 'item_updated':
      return (h.changes?.fields || []).map((f) => `${FIELD_LABEL[f.field] || f.field}: ${f.from || '—'} → ${f.to || '—'}`).join(' · ') || 'Updated';
    case 'due_date_changed':
      return `Due date ${fmtDate(h.changes?.from)} → ${fmtDate(h.changes?.to)}`;
    case 'reminder_sent': {
      const r = h.reminder || {};
      const who = (r.recipients || []).length;
      return `${TIER_LABEL[r.tier] || r.tier || 'Reminder'}${typeof r.daysBeforeDue === 'number' ? (r.daysBeforeDue >= 0 ? ` (${r.daysBeforeDue} days before due)` : ` (${Math.abs(r.daysBeforeDue)} days overdue)`) : ''} sent to ${who} ${who === 1 ? 'person' : 'people'}`;
    }
    case 'created':
      return `Added to the tracker${h.nextDueDate ? ` · due ${fmtDate(h.nextDueDate)}` : ''}`;
    default:
      return h.action;
  }
};

const HistoryPanel = ({ inspectionId, items = [], open, filterIndex, onFilterChange }) => {
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!inspectionId) return;
    setLoading(true);
    setError('');
    try {
      const data = await getInspectionHistory(inspectionId);
      setEntries(data);
    } catch (e) {
      setError('Could not load the history.');
    } finally {
      setLoading(false);
    }
  }, [inspectionId]);

  useEffect(() => { if (open && entries === null) load(); }, [open, entries, load]);

  const shown = useMemo(() => {
    if (!entries) return [];
    return filterIndex === 'all' ? entries : entries.filter((h) => Number(h.itemIndex) === Number(filterIndex));
  }, [entries, filterIndex]);

  if (!open) return null;

  return (
    <div className="it-history" role="region" aria-label="History">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FeatherIcon icon="clock" size={15} color="#1A6B4A" />
          <span style={{ fontWeight: 800, fontSize: '0.88rem', color: '#1A2D24' }}>History</span>
          {entries && <span className="it-header__badge it-header__badge--records" style={{ background: '#E8F5EE', color: '#1A6B4A' }}>{shown.length} event{shown.length !== 1 ? 's' : ''}</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Label for="it-history-filter" style={{ fontSize: '0.75rem', color: '#6B8F7C', margin: 0 }}>Show</Label>
          <Input id="it-history-filter" type="select" bsSize="sm" value={filterIndex} onChange={(e) => onFilterChange(e.target.value)} style={{ width: 220, borderColor: '#D4E8DC', borderRadius: 7, fontSize: '0.8rem' }}>
            <option value="all">All items</option>
            {items.map((it, i) => <option key={i} value={i}>{i + 1}. {it.inspectionTypeName || 'Item'}</option>)}
          </Input>
          <button type="button" className="it-btn it-btn--cancel" style={{ padding: '5px 10px' }} onClick={() => { setEntries(null); }} title="Refresh">
            <FeatherIcon icon="refresh-cw" size={12} />
          </button>
        </div>
      </div>

      {loading && <div style={{ padding: 16, textAlign: 'center' }}><Spinner size="sm" /> Loading history…</div>}
      {error && <div style={{ padding: 12, color: '#DC2626', fontSize: '0.82rem' }}>{error} <button type="button" className="it-btn it-btn--cancel" style={{ padding: '3px 8px', marginLeft: 8 }} onClick={load}>Retry</button></div>}
      {!loading && !error && entries && shown.length === 0 && (
        <div style={{ padding: 14, color: '#6B8F7C', fontSize: '0.82rem', textAlign: 'center' }}>Nothing recorded yet for this selection.</div>
      )}
      {!loading && shown.length > 0 && (
        <ul className="it-timeline">
          {shown.map((h) => {
            const meta = ACTION_META[h.action] || { icon: 'activity', color: '#6B8F7C', label: h.action };
            const itemName = h.inspectionTypeName || items[h.itemIndex]?.inspectionTypeName || '';
            const who = h.actor?.displayName || h.completedBy?.displayName || (h.actor?.role === 'system' ? 'System' : h.actor?.role || '');
            return (
              <li key={h._id} className="it-timeline__row">
                <span className="it-timeline__dot" style={{ background: meta.color }}><FeatherIcon icon={meta.icon} size={11} color="white" /></span>
                <div className="it-timeline__body">
                  <div className="it-timeline__head">
                    <span className="it-timeline__label" style={{ color: meta.color }}>{meta.label}</span>
                    {itemName && <span className="it-timeline__item">{itemName}</span>}
                    <span className="it-timeline__when">{fmtDateTime(h.at || h.createdAt)}{who ? ` · ${who}` : ''}</span>
                  </div>
                  <div className="it-timeline__text">{describeEntry(h)}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const STATUS_CHIP = {
  overdue: { text: 'Overdue', bg: '#FEF2F2', color: '#DC2626', border: '#FECDD3', icon: 'alert-circle' },
  due_soon: { text: 'Due soon', bg: '#FFFBEB', color: '#B45309', border: '#FDE68A', icon: 'clock' },
  skipped: { text: 'Skipped', bg: '#FEFCE8', color: '#A16207', border: '#FDE68A', icon: 'skip-forward' },
  done: { text: 'Done', bg: '#E8F5EE', color: '#1A6B4A', border: '#A7F3D0', icon: 'check-circle' },
  scheduled: { text: 'Scheduled', bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE', icon: 'calendar' },
  none: { text: 'No due date', bg: '#F7FAF8', color: '#6B8F7C', border: '#D4E8DC', icon: 'minus' },
};

// Card colours by status: red when overdue, yellow when skipped, green when done.
const CARD_TONE = {
  overdue: { border: '#FECDD3', bg: '#FEF2F2' },
  skipped: { border: '#FDE68A', bg: '#FEFCE8' },
  done: { border: '#D4E8DC', bg: '#F0FDF4' },
};

// How far the suite inspections have got this cycle, for types that count them.
const SuiteProgress = ({ progress: p }) => {
  const total = Number(p.totalUnits) || 0;
  const inspected = Number(p.inspectedUnits) || 0;
  const pct = total ? Math.round((inspected / total) * 100) : 0;
  const missing = Array.isArray(p.missing) ? p.missing : [];
  return (
    <div className="it-progress" role="group" aria-label="Suite inspection progress">
      <div className="it-progress__head">
        <span><FeatherIcon icon="home" size={12} /> {inspected} of {total} units inspected this cycle</span>
        <a href="/suite-inspections" target="_blank" rel="noreferrer">Open suite inspections</a>
      </div>
      <div className="it-progress__bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
        <div className="it-progress__fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="it-progress__foot">
        {total === 0
          ? 'No units found on the active mastersheet for this building.'
          : `Counting submitted inspections since ${fmtDate(p.windowStart)}. Completes automatically when every unit is inspected.${missing.length ? ` Still to do: ${missing.slice(0, 8).join(', ')}${missing.length > 8 ? '…' : ''}` : ''}`}
      </div>
    </div>
  );
};

const InspectionItemCard = ({
  item, index, inspectionId, isEditMode, typeOptions, frequencyOptions, onChange, onRemove, canRemove,
  canEditStructure, canRecordCompletion, onRecordUpdated, onShowHistory, onPendingFilesChange,
}) => {
  const onPendingPhotos = useCallback((files) => onPendingFilesChange?.(index, 'photo', files), [onPendingFilesChange, index]);
  const onPendingInvoices = useCallback((files) => onPendingFilesChange?.(index, 'invoice', files), [onPendingFilesChange, index]);
  const selectStyles = buildSelectStyles();
  const typeOption = typeOptions.find((o) => o.value === item.inspectionTypeId) || null;
  const freqOption = frequencyOptions.find((o) => o.value === item.frequencyId) || null;
  const status = itemStatus(item);
  const chip = STATUS_CHIP[status] || STATUS_CHIP.none;
  const tone = CARD_TONE[status] || { border: '#D4E8DC', bg: '#F7FAF8' };
  const fieldReadOnly = !canEditStructure;

  const handleField = (field, value) => {
    if (fieldReadOnly) return;
    onChange(index, { ...item, [field]: value });
  };

  const daysUntil = typeof item.daysUntilDue === 'number' ? item.daysUntilDue : null;
  const dueLine = item.dueDate
    ? `${status === 'overdue' ? 'Was due' : 'Due'} ${fmtDate(item.dueDate)}${daysUntil !== null ? ` · ${daysUntil < 0 ? `${Math.abs(daysUntil)} days overdue` : daysUntil === 0 ? 'today' : `in ${daysUntil} days`}` : ''}`
    : 'No due date';

  return (
    <div style={{ border: `1.5px solid ${tone.border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 12, background: tone.bg, position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#E8F5EE', border: '1px solid #D4E8DC', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 700, color: '#1A6B4A' }}>{index + 1}</div>
          {fieldReadOnly && <span style={{ fontWeight: 800, fontSize: '0.9rem', color: '#1A2D24' }}>{item.inspectionTypeName || 'Inspection'}</span>}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', fontWeight: 700, color: chip.color, background: chip.bg, border: `1px solid ${chip.border}`, borderRadius: 5, padding: '1px 8px' }}>
            <FeatherIcon icon={chip.icon} size={10} color={chip.color} /> {chip.text}
          </span>
          <span style={{ fontSize: '0.74rem', color: status === 'overdue' ? '#DC2626' : '#4A7060', fontWeight: 600 }}>{dueLine}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {isEditMode && (
            <button type="button" className="it-btn it-btn--cancel" style={{ padding: '4px 9px', fontSize: '0.72rem' }} onClick={() => onShowHistory(index)}>
              <FeatherIcon icon="clock" size={11} /> History
            </button>
          )}
          {canRemove && canEditStructure && (
            <button type="button" className="it-dbtn" onClick={() => onRemove(index)} title="Remove item" aria-label="Remove item">
              <FeatherIcon icon="trash-2" size={13} color="#DC2626" />
            </button>
          )}
        </div>
      </div>

      <Row className="g-3">
        <Col md={6}>
          <FormGroup>
            <Label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060' }}>Inspection Type *</Label>
            {fieldReadOnly ? (
              <div className="form-control form-control-sm" style={{ backgroundColor: '#f8f9fa', color: '#495057', fontSize: '0.82rem', borderColor: '#D4E8DC', borderRadius: 8 }}>
                {item.inspectionTypeName || '—'}
              </div>
            ) : (
              <Select
                options={typeOptions} value={typeOption}
                onChange={(o) => onChange(index, { ...item, inspectionTypeId: o?.value || '', inspectionTypeName: o?.label || '', contractor: item.contractor || (typeOptions.find((t) => t.value === o?.value)?.defaultContractor || '') })}
                styles={selectStyles} menuPortalTarget={document.body} placeholder="Select inspection type..." isClearable
                aria-label="Inspection type"
              />
            )}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060' }}>Frequency *</Label>
            {fieldReadOnly ? (
              <div className="form-control form-control-sm" style={{ backgroundColor: '#f8f9fa', color: '#495057', fontSize: '0.82rem', borderColor: '#D4E8DC', borderRadius: 8 }}>
                {item.frequencyName || '—'}
              </div>
            ) : (
              <Select
                options={frequencyOptions} value={freqOption}
                onChange={(o) => onChange(index, { ...item, frequencyId: o?.value || '', frequencyName: o?.label || '', frequencyDays: o?.days })}
                styles={selectStyles} menuPortalTarget={document.body} placeholder="Select frequency..." isClearable
                aria-label="Frequency"
              />
            )}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060' }}>Contractor *</Label>
            <Input type="text" bsSize="sm" value={item.contractor || ''} onChange={(e) => handleField('contractor', e.target.value)} placeholder={fieldReadOnly ? '—' : 'Contractor name...'} readOnly={fieldReadOnly}
              style={{ borderColor: '#D4E8DC', borderRadius: 7, fontSize: '0.82rem', backgroundColor: fieldReadOnly ? '#f8f9fa' : undefined }} />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060' }}>Due Date *</Label>
            <Input type="date" bsSize="sm" value={item.dueDate ? new Date(item.dueDate).toISOString().split('T')[0] : ''} onChange={(e) => handleField('dueDate', e.target.value)} readOnly={fieldReadOnly}
              style={{ borderColor: status === 'overdue' ? '#FECDD3' : '#D4E8DC', borderRadius: 7, fontSize: '0.82rem', backgroundColor: fieldReadOnly ? '#f8f9fa' : undefined }} />
            {!fieldReadOnly && item.frequencyDays && <small style={{ color: '#6B8F7C', fontSize: '0.72rem', fontStyle: 'italic' }}>Recording a completion moves this forward by {item.frequencyName || `${item.frequencyDays} days`}.</small>}
          </FormGroup>
        </Col>
        <Col md={12}>
          <FormGroup>
            <Label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060' }}>Notes</Label>
            <Input type="textarea" bsSize="sm" rows={2} value={item.notes || ''} onChange={(e) => handleField('notes', e.target.value)} placeholder={fieldReadOnly ? '—' : 'Any notes about this inspection item...'} readOnly={fieldReadOnly}
              style={{ borderColor: '#D4E8DC', borderRadius: 7, fontSize: '0.82rem', resize: 'vertical', backgroundColor: fieldReadOnly ? '#f8f9fa' : undefined }} />
          </FormGroup>
        </Col>
      </Row>

      {item.suiteProgress && typeof item.suiteProgress === 'object' && <SuiteProgress progress={item.suiteProgress} />}

      <div className="it-item-bottom" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginTop: 8, paddingTop: 12, borderTop: '1px solid #D4E8DC' }}>
        <div>
          <Label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060', display: 'block', marginBottom: 8 }}>Completion</Label>
          {isEditMode && inspectionId ? (
            <CompletionPanel item={item} index={index} inspectionId={inspectionId} canAct={canRecordCompletion} onRecordUpdated={onRecordUpdated} />
          ) : (
            <CompletedToggle item={item} onChange={(updated) => onChange(index, updated)} readOnly={!canRecordCompletion} />
          )}
        </div>
        <div>
          <Label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060', display: 'block', marginBottom: 8 }}>Reminders</Label>
          <ReminderDaysInput item={item} onChange={(updated) => onChange(index, updated)} readOnly={!canEditStructure} />
        </div>
        <div>
          <Label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060', display: 'block', marginBottom: 8 }}>Photos & files</Label>
          <AttachmentUploader
            kind="photo"
            title="Photos"
            itemIndex={index}
            existingAttachments={item.completed?.attachments || []}
            onAttachmentsChange={(keys) => onChange(index, { ...item, completed: { ...(item.completed || { notes: '' }), attachments: keys } })}
            onPendingChange={onPendingPhotos}
            inspectionId={isEditMode ? inspectionId : null}
            isEditMode={isEditMode}
            readOnly={!canRecordCompletion}
          />
          <div style={{ marginTop: 10 }}>
            <AttachmentUploader
              kind="invoice"
              title="Invoice"
              helper="PDF or photo of the contractor's invoice"
              itemIndex={index}
              existingAttachments={item.completed?.invoices || []}
              onAttachmentsChange={(keys) => onChange(index, { ...item, completed: { ...(item.completed || { notes: '' }), invoices: keys } })}
              onPendingChange={onPendingInvoices}
              inspectionId={isEditMode ? inspectionId : null}
              isEditMode={isEditMode}
              readOnly={!canRecordCompletion}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const InspectionTrackerFormModal = ({
  isOpen, onClose, onSave, inspectionData, isNew, propertyContext, typeOptions = [], frequencyOptions = [], userRole, userLead = false,
}) => {
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [touchedByAction, setTouchedByAction] = useState(false);
  const pendingFilesByItemRef = useRef({});
  const historyRef = useRef(null);

  const role = String(userRole || '').toLowerCase();
  const canEditStructure = role === 'company' || role === 'auditor' || (role === 'technical staff' && !!userLead);
  const canRecordCompletion = canEditStructure || role === 'manager' || role === 'technical staff';

  useEffect(() => {
    if (isOpen) {
      if (inspectionData) {
        setFormData({ ...inspectionData });
      } else {
        setFormData({ property: { propertyId: propertyContext?.propertyId || '', buildingName: propertyContext?.buildingName || '' }, year: new Date().getFullYear(), inspections: [{ ...defaultInspectionItem }] });
      }
      pendingFilesByItemRef.current = {};
      setHistoryOpen(false);
      setHistoryFilter('all');
      setTouchedByAction(false);
    }
  }, [isOpen, inspectionData, propertyContext]);

  const handleItemChange = useCallback((index, updatedItem) => {
    setFormData((prev) => { const items = [...(prev.inspections || [])]; items[index] = updatedItem; return { ...prev, inspections: items }; });
  }, []);

  // An immediate action (completion, notes, files) returned the fresh record:
  // adopt it so a later Save never sends stale completion data.
  const handleRecordUpdated = useCallback((record) => {
    if (!record) return;
    setFormData((prev) => ({ ...prev, ...record, inspections: record.inspections || prev.inspections }));
    setTouchedByAction(true);
  }, []);

  const addItem = () => {
    if ((formData?.inspections || []).length >= MAX_ITEMS) return;
    setFormData((prev) => ({ ...prev, inspections: [...(prev.inspections || []), { ...defaultInspectionItem }] }));
  };

  const removeItem = async (index) => {
    const r = await Swal.fire({ title: 'Remove this item?', text: 'It is removed from the record when you save. Its history stays.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#DC2626', confirmButtonText: 'Remove', reverseButtons: true });
    if (!r.isConfirmed) return;
    setFormData((prev) => { const items = [...(prev.inspections || [])]; items.splice(index, 1); return { ...prev, inspections: items }; });
    // Items after the removed one shift down by one; their pending files move with them.
    const np = {};
    for (const [k, v] of Object.entries(pendingFilesByItemRef.current)) {
      const i = Number(k);
      if (i < index) np[i] = v;
      else if (i > index) np[i - 1] = v;
    }
    pendingFilesByItemRef.current = np;
  };

  // Files picked on a brand-new record, per item and kind, uploaded once the record exists.
  const handlePendingFiles = useCallback((index, kind, files) => {
    const cur = pendingFilesByItemRef.current;
    cur[index] = { ...(cur[index] || {}), [kind]: files };
  }, []);

  const handleSave = async () => {
    if (!formData) return;
    if (!formData.year) { toast.error('Year is required'); return; }
    if (!formData.inspections?.length) { toast.error('At least one inspection item is required'); return; }

    const incomplete = formData.inspections.findIndex((i) => !i.inspectionTypeId || !i.frequencyId || !i.contractor?.trim() || !i.dueDate);
    if (incomplete >= 0) { toast.error(`Item ${incomplete + 1}: inspection type, frequency, contractor and due date are required`); return; }

    const payload = { ...formData, property: { propertyId: propertyContext.propertyId, buildingName: propertyContext.buildingName } };
    setSaving(true);
    try {
      const result = await onSave(payload, isNew);
      if (isNew && result?._id) {
        const newId = result._id;
        for (const [idxStr, byKind] of Object.entries(pendingFilesByItemRef.current)) {
          for (const kind of ['photo', 'invoice']) {
            const files = byKind?.[kind];
            if (!files?.length) continue;
            await uploadAttachmentFiles(newId, Number(idxStr), files, [], kind);
          }
        }
      }
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(`Failed to ${isNew ? 'create' : 'save'} inspection`);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    onClose(touchedByAction);
  };

  const showHistoryFor = (index) => {
    setHistoryFilter(String(index));
    setHistoryOpen(true);
    setTimeout(() => historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const items = formData?.inspections || [];
  const itemCount = items.length;
  const completedCount = items.filter((i) => itemStatus(i) === 'done').length;
  const overdueCount = items.filter((i) => itemStatus(i) === 'overdue').length;
  const skippedCount = items.filter((i) => itemStatus(i) === 'skipped' || isSkipped(i)).length;
  const isEditMode = !isNew && !!formData?._id;

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="xl" className="it-form-modal" style={{ maxWidth: 980 }} scrollable>
      <div className="it-header" style={{ borderRadius: '8px 8px 0 0' }}>
        <div className="it-header__left">
          <div className="it-header__icon-wrap">
            <FeatherIcon icon={isNew ? 'plus-circle' : (canEditStructure ? 'edit-2' : 'check-square')} size={18} color="#A7F3D0" />
          </div>
          <div className="it-header__meta">
            <div className="it-header__label">{isNew ? 'New Inspection Record' : canEditStructure ? 'Edit Inspection Record' : 'Inspection Record'}</div>
            <div className="it-header__title">
              {propertyContext?.name || 'Property'}
              {propertyContext?.buildingName ? ` — ${propertyContext.buildingName}` : ''}
              {propertyContext?.type ? ` (${propertyContext.type})` : ''}
            </div>
          </div>
          <div className="it-header__badges">
            {itemCount > 0 && <span className="it-header__badge it-header__badge--records">{itemCount} item{itemCount !== 1 ? 's' : ''}</span>}
            {completedCount > 0 && <span className="it-header__badge" style={{ background: '#E8F5EE', color: '#1A6B4A' }}>{completedCount} done</span>}
            {skippedCount > 0 && <span className="it-header__badge it-header__badge--skipped">{skippedCount} skipped</span>}
            {overdueCount > 0 && <span className="it-header__badge it-header__badge--overdue">{overdueCount} overdue</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isEditMode && (
            <button type="button" className="it-btn it-btn--ghost-light" style={{ padding: '5px 10px', fontSize: '0.75rem' }} onClick={() => { setHistoryOpen((o) => !o); if (!historyOpen) setTimeout(() => historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); }}>
              <FeatherIcon icon="clock" size={12} /> {historyOpen ? 'Hide history' : 'History'}
            </button>
          )}
          <button type="button" className="it-icon-btn" onClick={handleClose} aria-label="Close"><FeatherIcon icon="x" size={14} color="white" /></button>
        </div>
      </div>

      <ModalBody style={{ padding: '20px 24px', maxHeight: '75vh', overflowY: 'auto' }}>
        {!canEditStructure && !isNew && <ReadOnlyBanner canRecordCompletion={canRecordCompletion} />}

        <Row className="g-3 mb-4">
          <Col md={4}>
            <FormGroup>
              <Label style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1A6B4A' }}>Property</Label>
              <div className="form-control form-control-sm" style={{ backgroundColor: '#f8f9fa', color: '#495057', cursor: 'default', borderColor: '#D4E8DC', borderRadius: 8, fontSize: '0.82rem' }}>{propertyContext?.name || '—'}</div>
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1A6B4A' }}>Building</Label>
              <div className="form-control form-control-sm" style={{ backgroundColor: '#f8f9fa', color: '#495057', cursor: 'default', borderColor: '#D4E8DC', borderRadius: 8, fontSize: '0.82rem' }}>{propertyContext?.buildingName || '—'}</div>
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label for="it-year" style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1A6B4A' }}>Year *</Label>
              {canEditStructure ? (
                <Input id="it-year" type="number" bsSize="sm" value={formData?.year || ''} onChange={(e) => setFormData((prev) => ({ ...prev, year: Number(e.target.value) }))} min="2000" max="2100" placeholder="e.g. 2026" style={{ borderColor: '#D4E8DC', borderRadius: 8, fontSize: '0.82rem' }} />
              ) : (
                <div className="form-control form-control-sm" style={{ backgroundColor: '#f8f9fa', color: '#495057', cursor: 'default', borderColor: '#D4E8DC', borderRadius: 8, fontSize: '0.82rem' }}>{formData?.year || '—'}</div>
              )}
            </FormGroup>
          </Col>
        </Row>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FeatherIcon icon="list" size={16} color="#1A6B4A" />
            <span style={{ fontWeight: 800, fontSize: '0.9rem', color: '#1A2D24' }}>Inspection Items ({itemCount}/{MAX_ITEMS})</span>
          </div>
          {canEditStructure && (
            <button type="button" className="it-btn it-btn--add-item" onClick={addItem} disabled={itemCount >= MAX_ITEMS}>
              <FeatherIcon icon="plus" size={13} /> Add Item
            </button>
          )}
        </div>

        {items.map((item, idx) => (
          <InspectionItemCard
            key={idx}
            item={item}
            index={idx}
            inspectionId={formData?._id || null}
            isEditMode={isEditMode}
            typeOptions={typeOptions}
            frequencyOptions={frequencyOptions}
            onChange={handleItemChange}
            onRemove={removeItem}
            canRemove={itemCount > 1}
            canEditStructure={canEditStructure}
            canRecordCompletion={canRecordCompletion}
            onRecordUpdated={handleRecordUpdated}
            onShowHistory={showHistoryFor}
            onPendingFilesChange={handlePendingFiles}
          />
        ))}

        {itemCount === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 20px', color: '#6B8F7C' }}>
            <FeatherIcon icon="clipboard" size={32} color="#D4E8DC" />
            <div style={{ marginTop: 10, fontWeight: 600, fontSize: '0.9rem' }}>No inspection items yet</div>
            <div style={{ fontSize: '0.8rem', marginTop: 4 }}>Click "Add Item" to add inspection items</div>
          </div>
        )}

        <div ref={historyRef}>
          <HistoryPanel inspectionId={formData?._id} items={items} open={isEditMode && historyOpen} filterIndex={historyFilter} onFilterChange={setHistoryFilter} />
        </div>
      </ModalBody>

      <ModalFooter style={{ borderTop: '1px solid #D4E8DC', padding: '12px 24px', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="it-btn it-btn--cancel" onClick={handleClose} disabled={saving}>
          <FeatherIcon icon="x" size={13} /> {canEditStructure ? 'Cancel' : 'Close'}
        </button>
        {canEditStructure && (
          <button type="button" className={`it-btn it-btn--save${saving ? ' it-btn--saving' : ''}`} onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={13} />}
            {saving ? 'Saving...' : isNew ? 'Create Inspection' : 'Save Changes'}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export { InspectionTrackerFormModal };
